/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/layout/AdminLayout';


// Components
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Card from '../components/ui/Card';

// Tournament management components
import TournamentDetailsTab from '../components/admin/tournament/TournamentDetailsTab';
import TournamentPlayersTab from '../components/admin/tournament/TournamentPlayersTab';
import TournamentScoreEntryTab from '../components/admin/tournament/TournamentScoreEntryTab';
import TournamentParticipantsTab from '../components/admin/tournament/TournamentParticipantsTab';
import TournamentSettingsTab from '../components/admin/tournament/TournamentSettingsTab';

// API Services
import { 
  fetchTournaments,
  updateTournament,
  fetchTournamentPlayers,
  fetchAvailableGolfers,
  addPlayerToTournament,
  removePlayerFromTournament,
  updateTournamentPlayerTier,
  syncTournamentPlayersFromSlashGolf
} from '../components/admin/adminApiService';

// Styles
import './EditTournament.css';
import '../components/admin/tournament/TournamentTabs.css';

const EditTournament = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // State management
  const [activeTab, setActiveTab] = useState('details');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch tournament data
  const { data: tournaments, isLoading: tournamentsLoading } = useQuery({
    queryKey: ['admin-tournaments'],
    queryFn: fetchTournaments,
    refetchOnWindowFocus: false,
  });

  const tournament = tournaments?.find(t => t.id === id);

  // Fetch tournament players
  const { data: tournamentPlayers, isLoading: playersLoading } = useQuery({
    queryKey: ['tournament-players', id],
    queryFn: () => fetchTournamentPlayers(id),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  // Fetch available golfers
  const { data: availableGolfers, isLoading: golfersLoading } = useQuery({
    queryKey: ['golfers'],
    queryFn: fetchAvailableGolfers,
    refetchOnWindowFocus: false,
  });

  // Update tournament mutation
  const updateTournamentMutation = useMutation({
    mutationFn: updateTournament,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tournaments'] });
      setHasUnsavedChanges(false);
    },
  });

  // Player management mutations
  const addPlayerMutation = useMutation({
    mutationFn: ({ golferId, tier }) => addPlayerToTournament(id, golferId, tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament-players', id] });
    },
  });

  const removePlayerMutation = useMutation({
    mutationFn: (golferId) => removePlayerFromTournament(id, golferId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament-players', id] });
    },
  });

  const updateTierMutation = useMutation({
    mutationFn: ({ golferId, tier }) => updateTournamentPlayerTier(id, golferId, tier),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tournament-players', id] });
    },
  });

  // Handle navigation away with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Check if user is admin
  if (!user || user.role !== 'ADMIN') {
    return (
      <div className="access-denied">
        <Card>
          <h2>Access Denied</h2>
          <p>You don't have permission to edit tournaments.</p>
        </Card>
      </div>
    );
  }

  // Loading state
  if (tournamentsLoading) {
    return (
      <div className="edit-tournament-loading">
        <div className="edit-tournament-loading-spinner">
          <div className="edit-tournament-spinner"></div>
          Loading tournament...
        </div>
      </div>
    );
  }

  // Tournament not found
  if (!tournament) {
    return (
      <div className="tournament-not-found">
        <Card>
          <h2>Tournament Not Found</h2>
          <p>The requested tournament could not be found.</p>
        </Card>
      </div>
    );
  }

  const canModify = tournament.status === 'DRAFT' || tournament.status === 'ACTIVE' || tournament.status === 'LIVE';

  const tabs = [
    { id: 'details', label: 'Details', icon: '📝' },
    { id: 'players', label: 'Players', icon: '👥', count: tournamentPlayers?.length || 0 },
    { id: 'scores', label: 'Scores', icon: '⛳' },
    { id: 'participants', label: 'Participants', icon: '🎯' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  return (
    <AdminLayout>
      <div className="edit-tournament-page">
      <div className="edit-tournament-header">
        <div className="header-content">
          <div className="tournament-info">
            <h1>Edit Tournament: {tournament.name}</h1>
            <div className="tournament-meta">
              <span className={`status-badge status-${tournament.status?.toLowerCase()}`}>
                {tournament.status}
              </span>
              {!canModify && (
                <span className="modification-warning">
                  ⚠️ Tournament is LIVE - modifications not allowed
                </span>
              )}
              {hasUnsavedChanges && (
                <span className="unsaved-changes">● Unsaved changes</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="edit-tournament-content">
        <div className="tournament-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`edit-tournament-tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.count !== undefined && (
                <span className="tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'details' && (
            <TournamentDetailsTab
              tournament={tournament}
              canModify={canModify}
              updateMutation={updateTournamentMutation}
              onChanges={setHasUnsavedChanges}
            />
          )}

          {activeTab === 'players' && (
            <TournamentPlayersTab
              tournament={tournament}
              tournamentPlayers={tournamentPlayers || []}
              availableGolfers={availableGolfers || []}
              canModify={canModify}
              playersLoading={playersLoading}
              golfersLoading={golfersLoading}
              addPlayerMutation={addPlayerMutation}
              removePlayerMutation={removePlayerMutation}
              updateTierMutation={updateTierMutation}
              queryClient={queryClient}
            />
          )}

          {activeTab === 'scores' && (
            <TournamentScoreEntryTab
              tournament={tournament}
              tournamentPlayers={tournamentPlayers || []}
              canModify={canModify}
            />
          )}

          {activeTab === 'participants' && (
            <TournamentParticipantsTab
              tournament={tournament}
              canModify={canModify}
            />
          )}

          {activeTab === 'settings' && (
            <TournamentSettingsTab
              tournament={tournament}
              canModify={canModify}
              updateMutation={updateTournamentMutation}
              onChanges={setHasUnsavedChanges}
            />
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
};

export default EditTournament;